"use client";

/**
 * Blog Program Cards (variant 5) — narrow blog column rail:
 * - **Mobile (below sm):** centered snap, arrows on card borders; needs `containScroll: false`.
 * - **Desktop (sm+):** `align: start`, arrows **beside** the track (column-aligned); `trimSnaps`.
 */
import { cn } from "@/lib/utils";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

/** Card width target in the rail (~2 cards + gap in ~800px column with nav beside). */
export const BLOG_PROGRAM_CARD_WIDTH_PX = 290;

const NAV_BTN =
  "pointer-events-auto flex size-9 shrink-0 items-center justify-center rounded-full bg-[#BE2330] text-white shadow-[0_4px_14px_rgba(190,35,48,0.35)] transition-[opacity,transform,background-color] hover:opacity-95 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#BE2330] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-neutral-200 disabled:text-neutral-400 disabled:shadow-none disabled:opacity-90 disabled:hover:opacity-90 disabled:active:scale-100 sm:size-11";

/** Button center pinned to card edge; uses `--blog-pc-w` (same as slide width). */
const NAV_PREV_ON_CARD =
  "absolute left-[calc(50%_-_var(--blog-pc-w)/2)] top-1/2 z-20 -translate-x-1/2 -translate-y-1/2";
const NAV_NEXT_ON_CARD =
  "absolute left-[calc(50%_+_var(--blog-pc-w)/2)] top-1/2 z-20 -translate-x-1/2 -translate-y-1/2";

/** Fixed rail width so cards are not squeezed when nav sits beside the track (min(100%,…) was shrinking them). */
const SLIDE_W =
  "w-[var(--blog-pc-w)] max-w-[var(--blog-pc-w)] shrink-0 flex-none basis-[var(--blog-pc-w)]";

const carouselVars = {
  "--blog-pc-w": `${BLOG_PROGRAM_CARD_WIDTH_PX}px`,
} as CSSProperties;

const SM_UP_MQ = "(min-width: 640px)";

export type BlogProgramCardsCarouselProps<T> = {
  items: T[];
  renderSlide: (item: T, index: number) => ReactNode;
  className?: string;
};

export function BlogProgramCardsCarousel<T>({
  items,
  renderSlide,
  className,
}: BlogProgramCardsCarouselProps<T>) {
  const [isDesktop, setIsDesktop] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia(SM_UP_MQ);
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const emblaOpts = useMemo(
    () =>
      isDesktop
        ? {
            loop: false,
            align: "start" as const,
            containScroll: "trimSnaps" as const,
            dragFree: false,
          }
        : {
            loop: false,
            align: "center" as const,
            containScroll: false,
            dragFree: false,
          },
    [isDesktop],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOpts);
  const [nav, setNav] = useState(() => ({
    canPrev: false,
    canNext: items.length > 1,
  }));

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit(emblaOpts);
    if (!isDesktop) {
      const id = requestAnimationFrame(() => {
        emblaApi.scrollTo(emblaApi.selectedScrollSnap(), true);
      });
      return () => cancelAnimationFrame(id);
    }
  }, [emblaApi, items.length, emblaOpts, isDesktop]);

  const updateNav = useCallback(() => {
    if (!emblaApi) return;
    setNav({
      canPrev: emblaApi.canScrollPrev(),
      canNext: emblaApi.canScrollNext(),
    });
  }, [emblaApi]);

  useLayoutEffect(() => {
    if (!emblaApi) return;
    updateNav();
    emblaApi.on("reInit", updateNav);
    emblaApi.on("select", updateNav);
    emblaApi.on("scroll", updateNav);
    return () => {
      emblaApi.off("reInit", updateNav);
      emblaApi.off("select", updateNav);
      emblaApi.off("scroll", updateNav);
    };
  }, [emblaApi, updateNav]);

  const onPrev = useCallback(() => {
    if (!emblaApi?.canScrollPrev()) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNext = useCallback(() => {
    if (!emblaApi?.canScrollNext()) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const totalCount = items.length;
  const multi = totalCount > 1;
  const { canPrev, canNext } = nav;

  const slidesRow = (
    <div className={cn("flex touch-pan-y", multi && "gap-6")}>
      {items.map((item, index) => (
        <div
          key={index}
          className={cn(
            multi ? SLIDE_W : "min-w-0 w-full flex-[0_0_100%]",
          )}
        >
          {renderSlide(item, index)}
        </div>
      ))}
    </div>
  );

  if (!multi) {
    return (
      <div
        className={cn("w-full min-w-0", className)}
        style={carouselVars}
      >
        <div className="w-full overflow-hidden" ref={emblaRef}>
          {slidesRow}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative w-full min-w-0", className)} style={carouselVars}>
      <div className="flex w-full min-w-0 flex-col sm:flex-row sm:items-stretch sm:gap-2 md:gap-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canPrev}
          className={cn(NAV_BTN, "hidden self-center sm:flex")}
          aria-label="Previous program"
        >
          <ChevronLeft className="size-5 sm:size-6" strokeWidth={2.25} aria-hidden />
        </button>

        <div className="relative min-w-0 w-full sm:flex-1">
          <div className="min-w-0 w-full overflow-hidden" ref={emblaRef}>
            {slidesRow}
          </div>
          <button
            type="button"
            onClick={onPrev}
            disabled={!canPrev}
            className={cn(NAV_BTN, NAV_PREV_ON_CARD, "sm:hidden")}
            aria-label="Previous program"
          >
            <ChevronLeft className="size-5 sm:size-6" strokeWidth={2.25} aria-hidden />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canNext}
            className={cn(NAV_BTN, NAV_NEXT_ON_CARD, "sm:hidden")}
            aria-label="Next program"
          >
            <ChevronRight className="size-5 sm:size-6" strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <button
          type="button"
          onClick={onNext}
          disabled={!canNext}
          className={cn(NAV_BTN, "hidden self-center sm:flex")}
          aria-label="Next program"
        >
          <ChevronRight className="size-5 sm:size-6" strokeWidth={2.25} aria-hidden />
        </button>
      </div>
    </div>
  );
}
